import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';

import { useAuthStore } from '#ui-shared/stores/auth-store';

const INITIAL_RETRY_DELAY = 1000;
const MAX_RETRY_COUNT = 3;

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retryCount?: number;
}

let tokenRefreshPromise: Promise<void> | null = null;

async function refreshTokenOnce(authStore: ReturnType<typeof useAuthStore>): Promise<void> {
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  tokenRefreshPromise = authStore.checkAccessToken().finally(() => {
    tokenRefreshPromise = null;
  });

  return tokenRefreshPromise;
}

export function noraErrorHandlerResponseInterceptor() {
  const authStore = useAuthStore();

  const handleStatusBasedError = async (status: number, error: AxiosError): Promise<AxiosResponse | undefined> => {
    if (status === 401) {
      const originalRequest = error.config as CustomAxiosRequestConfig | undefined;

      if (!originalRequest) {
        return undefined;
      }

      if (originalRequest.url?.includes('/nexus/user/logout')) {
        return Promise.reject(error);
      }

      const retryCount = originalRequest._retryCount ?? 0;

      if (retryCount >= MAX_RETRY_COUNT) {
        console.debug('Max retries reached, logging out the user');
        await authStore.logout();
        return Promise.reject(error);
      }

      originalRequest._retryCount = retryCount + 1;

      if (retryCount > 0) {
        const delay = INITIAL_RETRY_DELAY * 2 ** retryCount;

        console.debug(
          `Retry ${retryCount} failed, waiting ${delay}ms before retrying again`,
        );

        await new Promise(resolve =>
          setTimeout(resolve, delay),
        );
      }
      else {
        console.debug(
          `Request to "${originalRequest.url}" failed, checking access token and trying again...`,
        );
      }

      await refreshTokenOnce(authStore);

      return axios(originalRequest);
    }

    console.error('Error http status returned, Axios error:');
    console.error(error);

    return undefined;
  };

  return async (error: AxiosError) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;

      if (status) {
        const statusBasedPromise = await handleStatusBasedError(status, error);

        if (statusBasedPromise) {
          return statusBasedPromise;
        }

        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  };
}
